import React, { ReactElement, useState } from 'react';
import {
  StyleSheet, Text, Pressable, View,
} from 'react-native';
import { useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import DeleteIcon24 from '../24pxIcons/DeleteIcon24';
import EditIcon24 from '../24pxIcons/EditIcon24';
import TemplateIcon from '../40pxIcons/TemplateIcon';
import AlertModal from '../AlertModal';
import AddTemplateModal from './AddTemplateModal';
import deleteTemplate from '../../requests/Assets/DeleteTemplate';
import { logEvent } from '../LogEvent';
import Style from '../../constants/Style';
import PressableStyled, { PressableState } from '../PressableStyled';
import {
  Template, TemplateGroup, TemplateType, TemplateTypeTitles,
  UserPermission,
} from '../../constants/Types';
import SensorIcon24 from '../24pxIcons/SensorIcon24';
import MeterIcon from '../24pxIcons/MeterIcon';
import { DELETE_TEMPLATE_FAILURE } from '../../constants/AlertMessages';

interface TemplateRowWebProps {
  template: Template,
  closeMenu: () => void,
  showParentAlert,
  deleteAction,
  update,
  templateGroups: TemplateGroup[],
  setSelectedTitle: () => void,
  setSelectedId: () => void,
  setLinkAssetsView: () => void,
}

export default function TemplateRowWeb({
  template, closeMenu, showParentAlert, deleteAction, update, templateGroups, setSelectedId,
  setSelectedTitle, setLinkAssetsView,
}: TemplateRowWebProps) {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canEditTemplates: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_UPDATE);
  const canDeleteTemplates: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_DELETE);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>('');
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>(null);

  const showAlert = (
    title: string,
    message: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = '',
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setLoading(false);
    setAlertVisible(true);
  };

  const deleteError = (text?: string) => showAlert(
    'Error',
    text ?? DELETE_TEMPLATE_FAILURE,
  );

  const editPress: () => void = () => {
    closeMenu();
    logEvent('TemplateGroupRowWeb', 'Edit');
    setEditModalVisible(true);
  };

  const deletePress: () => void = () => {
    closeMenu();
    logEvent('TemplateGroupRowWeb', 'Delete');
    showAlert(
      'Delete template',
      <>
        {'Are you sure you want to delete the template '}
        <Text style={Style.alertBoldText}>{template.name}</Text>
      </>,
      true,
      attemptDeletion,
      'Delete',
    );
  };

  const attemptDeletion: () => void = () => {
    setLoading(true);
    deleteTemplate(template.id).then((res: any) => {
      if (res && !res.isError) {
        showParentAlert(
          'Template successfully deleted',
          <Text>
            <Text style={Style.alertBoldText}>{template.name}</Text>
            {' was successfully deleted'}
          </Text>,
          false,
        );
        deleteAction(template.id);
      } else deleteError(res?.errorText);
    }).catch((error) => deleteError(error?.message))
      .finally(() => setLoading(false));
  };

  const select: () => void = () => {
    closeMenu();
    logEvent('TemplateGroupRowWeb', 'prototype_selected');
    setLinkAssetsView();
    setSelectedTitle();
    setSelectedId();
  };

  const renderIcon = (type: TemplateType) => {
    if (type === TemplateType.SENSOR) return <SensorIcon24 />;
    if (type === TemplateType.METER) return <MeterIcon stroke={'#fff'} />;
    return <TemplateIcon />;
  };

  return (
    <>
      <PressableStyled
        style={styles.container}
        onPress={select}
      >
        <View style={styles.header}>
          <View style={styles.icon}>
            {renderIcon(template.type)}
          </View>
          <View>
            <Text style={styles.title}>{template.name}</Text>
            <Text style={styles.type}>
              {TemplateTypeTitles[template.type] ?? TemplateTypeTitles[TemplateType.ASSET]}
            </Text>
          </View>
        </View>
        {canEditTemplates && <Pressable
          style={styles.editButton}
          onPress={editPress}
          testID='am-edit-template-btn'
        >
          <EditIcon24 />
        </Pressable>}
        {canDeleteTemplates && <Pressable
          style={({ hovered, pressed }: PressableState) => [
            styles.deleteButton, hovered && Style.listItemHovered, pressed && Style.listItemPressed,
          ]}
          onPress={deletePress}
          testID='am-delete-template-btn'
        >
          <DeleteIcon24 />
        </Pressable>}
      </PressableStyled>
      <AddTemplateModal
        templateGroups={templateGroups}
        isVisible={editModalVisible}
        hide={() => setEditModalVisible(false)}
        templateToUpdate={template}
        update={update}
        showParentAlert={showParentAlert}
      />
      <AlertModal
        modalVisible={alertVisible}
        title={alertTitle}
        message={alertMessage}
        error={alertError}
        action={alertAction}
        actionTitle={alertActionTitle}
        {...{ loading }}
        setModalVisible={setAlertVisible}
      />
    </>
  );
}

const styles = StyleSheet.create({
  icon: {
    marginEnd: 12,
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.light.hereworksDarkBlue,
  },
  title: {
    color: Colors.light.hereworksPrimaryGrey,
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 16,
    lineHeight: 24,
  },
  type: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  container: {
    paddingHorizontal: 18,
    paddingVertical: 19,
    borderRadius: 10,
    marginVertical: 2,
    marginHorizontal: 20,
    backgroundColor: Colors.light.hereworksWhite100,
    alignItems: 'center',
    flexDirection: 'row',
  },
  header: {
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
  },
  editButton: {
    position: 'absolute',
    right: 50,
  },
  deleteButton: {
    position: 'absolute',
    right: 20,
  },
});
